/**
 * Market Map - geospatial view of Nigerian markets (FR-500), rebuilt on the
 * F design system: palette-driven choropleth washes (accent up / danger
 * down), navy tooltip with mono figures, DeltaChips, honest UpdatedStamps
 * (nothing renders without a real date), keyboard-accessible states and
 * markers. Base tech unchanged: react-simple-maps + nigeria-states.json.
 */

import React, { useState, useMemo, useCallback } from 'react';
import {
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  Button,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  IconButton,
  Tooltip,
  Autocomplete,
  TextField,
  Collapse
} from '@mui/material';
import { Icon } from '@iconify/react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { ComposableMap, Geographies, Geography, Marker, ZoomableGroup } from 'react-simple-maps';
import { PageHeader, LoadingSkeleton, EmptyState, DeltaChip, UpdatedStamp } from '../../components/common';
import { useMarkets, useStates, usePriceMovements } from '../../services/queries';
import { useThemeMode, MONO, radius } from '../../context/ThemeContext';
import { useRecipes } from '../../theme/recipes';

const GEO_URL = '/data/nigeria-states.json';

const PILOT_STATE_NAMES = ['Kaduna', 'Taraba', 'Katsina', 'Bauchi'];

// Approximate [lng, lat] label anchors, nudged off marker clusters and
// state edges so the names stay legible.
const PILOT_STATE_CENTERS = {
  Kaduna: [7.05, 9.75],
  Katsina: [7.6, 12.5],
  Bauchi: [9.84, 10.31],
  Taraba: [10.9, 7.9],
};

// Alpha washes of the brand accent (#22C55E) and danger (#DA3B3B) tokens -
// same families as palette.accentBg / palette.dangerBg, stepped by intensity.
const UP_WASHES = ['rgba(34,197,94,0.14)', 'rgba(34,197,94,0.28)', 'rgba(34,197,94,0.45)'];
const DOWN_WASHES = ['rgba(218,59,59,0.10)', 'rgba(218,59,59,0.22)', 'rgba(218,59,59,0.38)'];

const washFor = (avg) => {
  if (avg > 5) return UP_WASHES[2];
  if (avg > 2) return UP_WASHES[1];
  if (avg > 0) return UP_WASHES[0];
  if (avg < -5) return DOWN_WASHES[2];
  if (avg < -2) return DOWN_WASHES[1];
  if (avg < 0) return DOWN_WASHES[0];
  return null; // stable -> neutral surface
};

function MarketMap({ embedded = false }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { palette } = useThemeMode();
  const recipes = useRecipes();

  const [selectedState, setSelectedState] = useState('all');
  const [selectedMarket, setSelectedMarket] = useState(null);
  const [hoveredState, setHoveredState] = useState(null);
  const [tooltipContent, setTooltipContent] = useState(null);
  const [tooltipPos, setTooltipPos] = useState({ x: 0, y: 0 });
  const [zoomLevel, setZoomLevel] = useState(1);
  const [center, setCenter] = useState([8, 9.5]);
  const [showGrid, setShowGrid] = useState(false);

  const { data: markets = [], isLoading: l1 } = useMarkets();
  const { data: pilotStates = [], isLoading: l2 } = useStates();
  const { data: priceMovements = [], isLoading: l3 } = usePriceMovements();
  const loading = l1 || l2 || l3;

  // Filter markets by state
  const filteredMarkets = useMemo(() => {
    if (selectedState === 'all') return markets;
    return markets.filter(m => m.state === selectedState);
  }, [selectedState, markets]);

  // Get selected market details
  const marketDetails = useMemo(() => {
    if (!selectedMarket) return null;
    const market = markets.find(m => m.id === selectedMarket);
    const priceData = priceMovements.find(p => p.marketId === selectedMarket);
    return { ...market, ...priceData };
  }, [selectedMarket, markets, priceMovements]);

  // Market statistics by state; lastUpdate is the NEWEST real movement date
  // in that state (no fabricated fallback - the stamp stays silent instead)
  const stateStats = useMemo(() => {
    return pilotStates.map(state => {
      const stateKey = state.name.split(' ')[0];
      const stateMovements = priceMovements.filter(p =>
        markets.find(m => m.id === p.marketId)?.state.includes(stateKey)
      );

      const avgChange = stateMovements.length > 0
        ? stateMovements.reduce((sum, m) => sum + m.priceChange, 0) / stateMovements.length
        : 0;

      let lastUpdate = null;
      for (const m of stateMovements) {
        if (m.lastUpdate && (!lastUpdate || m.lastUpdate > lastUpdate)) lastUpdate = m.lastUpdate;
      }

      return {
        ...state,
        stateKey,
        avgPriceChange: Number(avgChange.toFixed(1)),
        marketCount: stateMovements.length,
        trending: avgChange > 0 ? 'up' : avgChange < 0 ? 'down' : 'stable',
        lastUpdate
      };
    });
  }, [pilotStates, markets, priceMovements]);

  const statByName = useCallback(
    (stateName) => stateStats.find(s => s.stateKey === stateName),
    [stateStats]
  );

  // Base map is a visible gray so the whole country reads on the white
  // canvas; pilot states pop as white (stable) or a movement wash, with a
  // navy outline marking them as tracked.
  const stateFill = useCallback((stateName, { emphasis = false } = {}) => {
    if (!PILOT_STATE_NAMES.includes(stateName)) return emphasis ? palette.cardBorder : palette.surfaceHover;
    const stat = statByName(stateName);
    const wash = stat ? washFor(stat.avgPriceChange) : null;
    if (!wash) return emphasis ? palette.accentBg : palette.card;
    return wash;
  }, [palette, statByName]);

  const markerColor = (priceChange) => {
    if (priceChange > 0) return palette.accent;
    if (priceChange < 0) return palette.danger;
    return palette.textMuted;
  };

  const trendIcon = (trending, size = 18) => {
    if (trending === 'up') return <Icon icon="ph:trend-up" width={size} height={size} color={palette.accent} />;
    if (trending === 'down') return <Icon icon="ph:trend-down" width={size} height={size} color={palette.danger} />;
    return <Icon icon="ph:minus" width={size} height={size} color={palette.textMuted} />;
  };

  const handleZoomIn = useCallback(() => {
    setZoomLevel(prev => Math.min(prev * 1.5, 8));
  }, []);

  const handleZoomOut = useCallback(() => {
    setZoomLevel(prev => Math.max(prev / 1.5, 1));
  }, []);

  const selectStateByName = useCallback((stateName) => {
    if (PILOT_STATE_NAMES.includes(stateName)) {
      setSelectedState(stateName);
      setSelectedMarket(null);
    }
  }, []);

  const handleMouseMove = useCallback((e) => {
    setTooltipPos({ x: e.clientX, y: e.clientY });
  }, []);

  const handleGeographyMouseEnter = useCallback((geo) => {
    const stateName = geo.properties.NAME_1 || geo.properties.name;
    if (PILOT_STATE_NAMES.includes(stateName)) {
      const stat = statByName(stateName);
      setTooltipContent({
        name: stateName,
        marketCount: stat?.marketCount || 0,
        avgChange: stat?.avgPriceChange ?? 0
      });
      setHoveredState(stateName);
    }
  }, [statByName]);

  const handleGeographyMouseLeave = useCallback(() => {
    setTooltipContent(null);
    setHoveredState(null);
  }, []);

  // Build market markers for the map
  const marketMarkers = useMemo(() => {
    return filteredMarkets.map(market => {
      const priceData = priceMovements.find(p => p.marketId === market.id);
      return {
        ...market,
        coordinates: [market.lng, market.lat],
        priceChange: priceData?.priceChange ?? 0
      };
    });
  }, [filteredMarkets, priceMovements]);

  const risingCount = priceMovements.filter(p => p.priceChange > 0).length;
  const fallingCount = priceMovements.filter(p => p.priceChange < 0).length;

  const countPillSx = (color) => ({
    display: 'inline-flex',
    alignItems: 'center',
    gap: 0.6,
    px: 1.2,
    py: 0.45,
    borderRadius: `${radius.pill}px`,
    border: `1px solid ${palette.cardBorder}`,
    bgcolor: palette.card,
    fontSize: '0.74rem',
    fontWeight: 600,
    color: color || palette.textSecondary,
    whiteSpace: 'nowrap',
    '& .num': { fontFamily: MONO, fontWeight: 700 }
  });

  const legendItems = [
    { swatch: UP_WASHES[2], label: 'Up > 5%' },
    { swatch: UP_WASHES[1], label: 'Up 2-5%' },
    { swatch: UP_WASHES[0], label: 'Up 0-2%' },
    { swatch: palette.card, label: 'Stable', bordered: true },
    { swatch: DOWN_WASHES[0], label: 'Down 0-2%' },
    { swatch: DOWN_WASHES[1], label: 'Down 2-5%' },
    { swatch: DOWN_WASHES[2], label: 'Down > 5%' }
  ];

  if (loading) {
    return embedded ? (
      <LoadingSkeleton variant="page" />
    ) : (
      <Box>
        <PageHeader
          title="Market Map"
          subtitle="Interactive geospatial view of Nigerian commodity markets"
          breadcrumbs={[{ label: 'Home', path: '/' }, { label: 'Market Map' }]}
        />
        <LoadingSkeleton variant="page" />
      </Box>
    );
  }

  return (
    <Box>
      {!embedded && (
        <PageHeader
          eyebrow="Analytics"
          title="Market Map"
          subtitle="Interactive geospatial view of Nigerian commodity markets"
          breadcrumbs={[
            { label: 'Home', path: '/' },
            { label: 'Market Map' }
          ]}
          actions={
            <Button
              variant="outlined"
              size="small"
              startIcon={<Icon icon="ph:arrows-clockwise" width={16} height={16} />}
              onClick={() => queryClient.invalidateQueries()}
            >
              Refresh Data
            </Button>
          }
        />
      )}

      {/* Controls strip */}
      <Card sx={{ mb: 3 }}>
        <CardContent sx={{ py: 2, '&:last-child': { pb: 2 } }}>
          <Grid container spacing={1.5} alignItems="center">
            <Grid item xs={12} sm={6} md={3}>
              <Autocomplete
                size="small"
                options={[{ id: 'all', label: 'All pilot states' }, ...pilotStates.map(s => ({ id: s.name.split(' ')[0], label: s.name }))]}
                getOptionLabel={(o) => o.label}
                value={
                  selectedState === 'all'
                    ? { id: 'all', label: 'All pilot states' }
                    : { id: selectedState, label: pilotStates.find(s => s.name.split(' ')[0] === selectedState)?.name || selectedState }
                }
                onChange={(_, opt) => {
                  setSelectedState(opt ? opt.id : 'all');
                  setSelectedMarket(null);
                }}
                isOptionEqualToValue={(o, v) => o.id === v.id}
                disableClearable
                renderInput={(params) => <TextField {...params} label="State" sx={recipes.searchSx} />}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Autocomplete
                size="small"
                options={filteredMarkets}
                getOptionLabel={(m) => `${m.name} - ${m.state}`}
                value={filteredMarkets.find((m) => m.id === selectedMarket) || null}
                onChange={(_, market) => setSelectedMarket(market ? market.id : null)}
                isOptionEqualToValue={(o, v) => o.id === v.id}
                renderInput={(params) => <TextField {...params} label="Jump to market" sx={recipes.searchSx} />}
                clearOnEscape
              />
            </Grid>
            <Grid item xs={12} md={5}>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: { md: 'flex-end' } }}>
                <Box sx={countPillSx()}>
                  <Icon icon="ph:map-pin" width={14} height={14} />
                  <span className="num">{filteredMarkets.length}</span> markets
                </Box>
                <Box sx={countPillSx(palette.accent)}>
                  <Icon icon="ph:trend-up" width={14} height={14} />
                  <span className="num">{risingCount}</span> rising
                </Box>
                <Box sx={countPillSx(palette.danger)}>
                  <Icon icon="ph:trend-down" width={14} height={14} />
                  <span className="num">{fallingCount}</span> falling
                </Box>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Grid container spacing={3}>
        {/* Map Container */}
        <Grid item xs={12} lg={8}>
          <Card sx={{ position: 'relative' }}>
            <Box
              sx={{
                px: 2.5,
                py: 1.75,
                display: 'flex',
                alignItems: 'flex-start',
                justifyContent: 'space-between',
                gap: 1,
                borderBottom: `1px solid ${palette.divider}`
              }}
            >
              <Box>
                <Typography sx={{ fontWeight: 700, fontSize: '0.95rem' }}>Nigeria market distribution</Typography>
                <Typography sx={{ fontSize: '0.75rem', color: palette.textMuted }}>
                  Click a pilot state or market marker to view details
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 0.75 }}>
                <Tooltip title="Zoom in">
                  <IconButton size="small" aria-label="Zoom in" onClick={handleZoomIn} sx={recipes.ghostIconSx}>
                    <Icon icon="ph:plus" width={16} height={16} />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Zoom out">
                  <IconButton size="small" aria-label="Zoom out" onClick={handleZoomOut} sx={recipes.ghostIconSx}>
                    <Icon icon="ph:minus" width={16} height={16} />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>
            <CardContent
              sx={{ p: 1, position: 'relative', '&:last-child': { pb: 1 } }}
              onMouseMove={handleMouseMove}
            >
              <Box sx={{ height: { xs: 360, sm: 440, lg: 520 } }}>
                <ComposableMap
                  projection="geoMercator"
                  projectionConfig={{ center: [8, 9.5], scale: 2800 }}
                  style={{ width: '100%', height: '100%', backgroundColor: palette.card }}
                >
                  <ZoomableGroup
                    zoom={zoomLevel}
                    center={center}
                    onMoveEnd={({ coordinates, zoom }) => {
                      setCenter(coordinates);
                      setZoomLevel(zoom);
                    }}
                  >
                    <Geographies geography={GEO_URL}>
                      {({ geographies }) =>
                        geographies.map((geo) => {
                          const stateName = geo.properties.NAME_1 || geo.properties.name;
                          const isPilot = PILOT_STATE_NAMES.includes(stateName);
                          const isSelected = selectedState === stateName;
                          const isHovered = hoveredState === stateName;
                          const stat = isPilot ? statByName(stateName) : null;

                          return (
                            <Geography
                              key={geo.rsmKey || geo.properties.NAME_1 || geo.properties.name}
                              geography={geo}
                              tabIndex={isPilot ? 0 : -1}
                              role={isPilot ? 'button' : undefined}
                              aria-label={
                                isPilot
                                  ? `${stateName} State, ${stat?.marketCount || 0} markets, ${stat?.avgPriceChange >= 0 ? '+' : ''}${stat?.avgPriceChange ?? 0} percent 7-day average`
                                  : undefined
                              }
                              onKeyDown={(e) => {
                                if (isPilot && (e.key === 'Enter' || e.key === ' ')) {
                                  e.preventDefault();
                                  selectStateByName(stateName);
                                }
                              }}
                              fill={stateFill(stateName, { emphasis: isHovered || isSelected })}
                              stroke={isSelected ? palette.accent : isPilot ? 'rgba(15,39,68,0.45)' : palette.card}
                              strokeWidth={isSelected ? 1.5 : isPilot ? 0.9 : 0.6}
                              style={{
                                default: { outline: 'none' },
                                hover: {
                                  outline: 'none',
                                  fill: stateFill(stateName, { emphasis: true }),
                                  cursor: isPilot ? 'pointer' : 'default'
                                },
                                pressed: { outline: 'none' }
                              }}
                              onClick={() => selectStateByName(stateName)}
                              onMouseEnter={() => handleGeographyMouseEnter(geo)}
                              onMouseLeave={handleGeographyMouseLeave}
                            />
                          );
                        })
                      }
                    </Geographies>

                    {/* Pilot state name labels */}
                    {PILOT_STATE_NAMES.map((name) => (
                      <Marker key={`label-${name}`} coordinates={PILOT_STATE_CENTERS[name]}>
                        <text
                          textAnchor="middle"
                          y={-14 / zoomLevel}
                          style={{
                            fontFamily: MONO,
                            fontSize: `${9.5 / zoomLevel}px`,
                            fontWeight: 700,
                            letterSpacing: '0.1em',
                            fill: palette.navy,
                            pointerEvents: 'none',
                          }}
                        >
                          {name.toUpperCase()}
                        </text>
                      </Marker>
                    ))}

                    {/* Market Markers */}
                    {marketMarkers.map((market) => {
                      const isSelected = selectedMarket === market.id;
                      const color = markerColor(market.priceChange);
                      return (
                        <Marker
                          key={market.id}
                          coordinates={market.coordinates}
                          onClick={() => setSelectedMarket(market.id)}
                          style={{ cursor: 'pointer' }}
                        >
                          <g
                            role="button"
                            tabIndex={0}
                            aria-label={`${market.name} market, ${market.priceChange >= 0 ? '+' : ''}${market.priceChange} percent 7-day change`}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                setSelectedMarket(market.id);
                              }
                            }}
                            style={{ outline: 'none' }}
                          >
                            {/* Pulse ring for selected marker */}
                            {isSelected && (
                              <circle r={12 / zoomLevel} fill={color} opacity={0.3}>
                                <animate attributeName="r" from={8 / zoomLevel} to={18 / zoomLevel} dur="1.2s" repeatCount="indefinite" />
                                <animate attributeName="opacity" from="0.5" to="0" dur="1.2s" repeatCount="indefinite" />
                              </circle>
                            )}
                            {/* Main marker circle */}
                            <circle
                              r={(isSelected ? 6 : 4) / zoomLevel}
                              fill={color}
                              stroke={palette.card}
                              strokeWidth={1.5 / zoomLevel}
                            />
                            {/* Label for selected marker */}
                            {isSelected && (
                              <text
                                textAnchor="middle"
                                y={-10 / zoomLevel}
                                style={{
                                  fontSize: `${10 / zoomLevel}px`,
                                  fontWeight: 600,
                                  fill: palette.text,
                                  pointerEvents: 'none'
                                }}
                              >
                                {market.name}
                              </text>
                            )}
                          </g>
                        </Marker>
                      );
                    })}
                  </ZoomableGroup>
                </ComposableMap>
              </Box>

              {/* Navy floating tooltip with mono figures */}
              {tooltipContent && (
                <Box
                  sx={{
                    position: 'fixed',
                    left: tooltipPos.x + 14,
                    top: tooltipPos.y - 64,
                    px: 1.5,
                    py: 1,
                    pointerEvents: 'none',
                    zIndex: 1500,
                    maxWidth: 240,
                    bgcolor: palette.tooltipBg,
                    color: '#fff',
                    borderRadius: `${radius.control}px`,
                    boxShadow: palette.shadowLg
                  }}
                >
                  <Typography sx={{ fontWeight: 700, fontSize: '0.8rem', color: '#fff' }}>
                    {tooltipContent.name} State
                  </Typography>
                  <Typography sx={{ fontSize: '0.7rem', color: 'rgba(255,255,255,0.72)' }}>
                    {tooltipContent.marketCount} market{tooltipContent.marketCount !== 1 ? 's' : ''} tracked
                  </Typography>
                  <Typography
                    sx={{
                      fontFamily: MONO,
                      fontSize: '0.75rem',
                      fontWeight: 600,
                      mt: 0.25,
                      color: tooltipContent.avgChange > 0 ? '#4ADE80' : tooltipContent.avgChange < 0 ? '#F87171' : 'rgba(255,255,255,0.72)'
                    }}
                  >
                    {tooltipContent.avgChange >= 0 ? '+' : ''}{tooltipContent.avgChange}% 7-day avg
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>

          {/* Legend Card below the map */}
          <Card sx={{ mt: 2 }}>
            <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
              <Typography sx={{ ...recipes.statLabelSx, mb: 1 }}>
                Price movement legend (7-day change)
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                {legendItems.map(item => (
                  <Box key={item.label} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Box
                      sx={{
                        width: 12,
                        height: 12,
                        borderRadius: '3px',
                        bgcolor: item.swatch,
                        border: `1px solid ${item.bordered ? palette.cardBorder : 'transparent'}`
                      }}
                    />
                    <Typography sx={{ fontSize: '0.7rem', fontFamily: MONO, color: palette.textSecondary }}>
                      {item.label}
                    </Typography>
                  </Box>
                ))}
                <Divider orientation="vertical" flexItem sx={{ mx: 1, borderColor: palette.divider }} />
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Box
                    sx={{
                      width: 12,
                      height: 12,
                      borderRadius: '3px',
                      bgcolor: palette.surfaceHover,
                      border: `1px solid ${palette.cardBorder}`
                    }}
                  />
                  <Typography sx={{ fontSize: '0.7rem', fontFamily: MONO, color: palette.textSecondary }}>
                    Non-pilot state
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Sidebar */}
        <Grid item xs={12} lg={4}>
          {/* Market Details */}
          <Card sx={{ mb: 2 }}>
            <Box sx={{ px: 2.5, py: 1.75, display: 'flex', alignItems: 'center', gap: 1.25, borderBottom: `1px solid ${palette.divider}` }}>
              <Box sx={recipes.iconTileSx}>
                <Icon icon="ph:storefront" width={17} height={17} />
              </Box>
              <Typography sx={{ fontWeight: 700, fontSize: '0.95rem' }}>Market details</Typography>
            </Box>
            <CardContent>
              {marketDetails ? (
                <Box>
                  <Typography variant="h6" gutterBottom>
                    {marketDetails.name}
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2" sx={{ color: palette.textSecondary }}>State</Typography>
                      <Typography variant="body2" fontWeight={600}>{marketDetails.state}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2" sx={{ color: palette.textSecondary }}>LGA</Typography>
                      <Typography variant="body2" fontWeight={600}>{marketDetails.lga}</Typography>
                    </Box>
                    <Divider sx={{ borderColor: palette.divider }} />
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="body2" sx={{ color: palette.textSecondary }}>7-day change</Typography>
                      {typeof marketDetails.priceChange === 'number' ? (
                        <DeltaChip value={marketDetails.priceChange} />
                      ) : (
                        <Typography variant="body2" sx={{ color: palette.textMuted }}>-</Typography>
                      )}
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2" sx={{ color: palette.textSecondary }}>Top Commodity</Typography>
                      <Typography variant="body2" fontWeight={600}>{marketDetails.topCommodity || '-'}</Typography>
                    </Box>
                    <Box>
                      <UpdatedStamp date={marketDetails.lastUpdate} />
                      <Typography variant="caption" sx={{ color: palette.textSecondary, display: 'block' }}>
                        Source: Field Agents
                      </Typography>
                    </Box>
                    <Divider sx={{ borderColor: palette.divider }} />
                    <Button
                      variant="outlined"
                      fullWidth
                      size="small"
                      onClick={() => navigate('/app/markets?tab=records')}
                      sx={{
                        borderColor: palette.cardBorder,
                        color: palette.text,
                        fontWeight: 600,
                        '&:hover': { borderColor: palette.accent, bgcolor: palette.accentBg }
                      }}
                    >
                      View Market Prices
                    </Button>
                  </Box>
                </Box>
              ) : (
                <EmptyState
                  compact
                  icon={<Icon icon="ph:map-pin" width={24} height={24} />}
                  title="No market selected"
                  description="Click a marker on the map or pick a market from the list to see its details."
                />
              )}
            </CardContent>
          </Card>

          {/* State Overview */}
          <Card>
            <Box sx={{ px: 2.5, py: 1.75, borderBottom: `1px solid ${palette.divider}` }}>
              <Typography sx={{ fontWeight: 700, fontSize: '0.95rem' }}>State overview</Typography>
            </Box>
            <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
              <List disablePadding>
                {stateStats.map((state, index) => (
                  <React.Fragment key={state.id}>
                    <ListItemButton
                      selected={selectedState === state.stateKey}
                      onClick={() => setSelectedState(state.stateKey)}
                      sx={{
                        py: 1.25,
                        '&.Mui-selected': { bgcolor: palette.accentBg, '&:hover': { bgcolor: palette.accentBg } },
                        '&:hover': { bgcolor: palette.cardHover }
                      }}
                    >
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        {trendIcon(state.trending)}
                      </ListItemIcon>
                      <ListItemText
                        primary={state.name}
                        secondary={
                          <>
                            {`${state.marketCount} market${state.marketCount !== 1 ? 's' : ''}`}
                            <UpdatedStamp date={state.lastUpdate} component="span" sx={{ display: 'block' }} />
                          </>
                        }
                        primaryTypographyProps={{ fontSize: '0.88rem', fontWeight: 600 }}
                        secondaryTypographyProps={{ component: 'span', fontSize: '0.74rem' }}
                      />
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6 }}>
                        <DeltaChip value={state.avgPriceChange} />
                        <Typography sx={{ fontSize: '0.66rem', color: palette.textMuted, fontFamily: MONO }}>7d</Typography>
                      </Box>
                    </ListItemButton>
                    {index < stateStats.length - 1 && <Divider component="li" sx={{ borderColor: palette.divider }} />}
                  </React.Fragment>
                ))}
              </List>
            </CardContent>
          </Card>
        </Grid>

        {/* Market List - collapsed by default; the "Jump to market" field covers quick navigation */}
        <Grid item xs={12}>
          <Card>
            <Box
              sx={{
                px: 2.5,
                py: 1.75,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 1,
                borderBottom: showGrid ? `1px solid ${palette.divider}` : 'none'
              }}
            >
              <Box>
                <Typography sx={{ fontWeight: 700, fontSize: '0.95rem' }}>All markets</Typography>
                <Typography sx={{ fontSize: '0.75rem', color: palette.textMuted }}>
                  Showing {filteredMarkets.length} market{filteredMarkets.length !== 1 ? 's' : ''}
                </Typography>
              </Box>
              <Button
                size="small"
                variant="text"
                onClick={() => setShowGrid((v) => !v)}
                sx={{ textTransform: 'none', fontWeight: 600, color: palette.accent, '&:hover': { bgcolor: palette.accentBg } }}
              >
                {showGrid ? 'Hide grid' : 'Show grid'}
              </Button>
            </Box>
            <Collapse in={showGrid} timeout="auto" unmountOnExit>
              <CardContent sx={{ p: 1.5 }}>
                <Grid container spacing={1.5}>
                  {filteredMarkets.map((market) => {
                    const priceData = priceMovements.find(p => p.marketId === market.id);
                    const isSelected = selectedMarket === market.id;
                    return (
                      <Grid item xs={12} sm={6} md={4} lg={3} key={market.id}>
                        <Box
                          role="button"
                          tabIndex={0}
                          aria-pressed={isSelected}
                          onClick={() => setSelectedMarket(market.id)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              setSelectedMarket(market.id);
                            }
                          }}
                          sx={{
                            p: 2,
                            height: '100%',
                            cursor: 'pointer',
                            borderRadius: `${radius.control}px`,
                            border: `1.5px solid ${isSelected ? palette.accent : palette.cardBorder}`,
                            bgcolor: isSelected ? palette.accentBg : palette.card,
                            transition: 'border-color 0.15s ease, background-color 0.15s ease',
                            '&:hover': { borderColor: isSelected ? palette.accent : palette.textMuted, bgcolor: isSelected ? palette.accentBg : palette.cardHover },
                            '&:focus-visible': recipes.focusRingSx
                          }}
                        >
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1 }}>
                            <Box sx={{ minWidth: 0 }}>
                              <Typography variant="subtitle2" fontWeight={600} noWrap>
                                {market.name}
                              </Typography>
                              <Typography variant="caption" sx={{ color: palette.textSecondary }}>
                                {market.state} · {market.lga}
                              </Typography>
                            </Box>
                            {priceData && <DeltaChip value={priceData.priceChange} />}
                          </Box>
                          <UpdatedStamp date={market.lastUpdate || priceData?.lastUpdate} sx={{ display: 'block', mt: 0.5 }} />
                        </Box>
                      </Grid>
                    );
                  })}
                </Grid>
              </CardContent>
            </Collapse>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
}

export default MarketMap;
